import useTranslate from 'hooks/useTranslate';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
	useBucketUrlMutation,
	useGetCommonDataQuery,
	useGetTemplateDetailsQuery,
	useSaveResumeMutation,
	useUploadFileMutation,
} from 'services';

import {
	Achievements,
	CertificationsInfo,
	CustomProgressBar,
	EducationInfo,
	Experience,
	Hobbies,
	Language,
	PersonalInfo,
	Profile,
	ProjectsInfo,
	ShortBio,
	Skills,
	SocialMedia,
	Tools,
} from './ResumeFields';
import ConfirmModal from './ResumeFields/CommonComponents/ConfirmModal';
import { debounce } from 'lodash-es';
import { SLUGS } from 'constants';
import useResumeStore, { updateResumeData, updateResumeLimits } from 'store/resume';
import { ARRAY_FIELDS, RESUME_DEFAULT_VALUES } from 'constants/resumeForm';
import { compressFileSize, imageBucketUrl } from 'utils';
import useAuthStore from 'store/auth';
import './styles.scss';

const ResumeForm = () => {
	// store
	const { resumeData, resumeLimits } = useResumeStore((state: any) => state);
	const { user: userInfo }: Record<string, any> = useAuthStore((state) => state?.userInfo);
	const {
		profile,
		details,
		shortBio,
		experience,
		project,
		education,
		skills,
		tools,
		awards,
		certification,
		language,
		social,
		hobbies,
	} = resumeData;

	// hooks
	const { slug } = useParams();
	const templateSlug = Number(slug);
	const { translate } = useTranslate();

	// mutation
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const { mutateAsync: saveResumeMutation } = useSaveResumeMutation();

	// query
	const { data: commonData } = useGetCommonDataQuery();
	const { data: templateData } = useGetTemplateDetailsQuery(templateSlug);

	// state
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const [removeAlertData, setRemoveAlertData] = useState<any>({
		deleteIndex: undefined,
		deleteSlug: undefined,
		formikObj: undefined,
	});
	const [stateResumeForm, setStateResumeForm] = useState<any>({});
	const [isUploading, setIsUploading] = useState<any>({});
	const [fieldType, setFieldType] = useState<string | null>(null);

	const {
		profilePicture: SHOW_PROFILE_PICTURE,
		details: SHOW_DETAILS,
		shortBio: SHOW_SHORTBIO,
		experience: SHOW_EXPERIENCE,
		experienceImage: SHOW_EXPERIENCE_IMAGE,
		experienceRoleAndResponsibilities: SHOW_EXPERIENCE_ROLEANDRESPONSIBILITIES,
		projects: SHOW_PROJECT,
		education: SHOW_EDUCATION,
		educationImage: SHOW_EDUCATION_IMAGE,
		skills: SHOW_SKILLS,
		tools: SHOW_TOOLS,
		languages: SHOW_LANGUAGE,
		social: SHOW_SOCIAL,
		certifications: SHOW_CERTIFICATION,
		certificationsImage: SHOW_CERTIFICATIONS_IMAGE,
		hobbies: SHOW_HOBBIES,
		awards: SHOW_AWARDS,
		awardImage: SHOW_AWARD_IMAGE,
	} = templateData?.templateFormOptions ?? {};

	useEffect(() => {
		if (templateData && Object.keys(templateData)?.length) {
			const dummyData = { ...templateData };
			const userData = {
				firstName: dummyData?.details?.firstName ?? userInfo?.name,
				lastName: dummyData?.details?.lastName ?? userInfo?.lastName,
				email: dummyData?.details?.email ?? userInfo?.email,
				countryCode: dummyData?.details?.countryCode ?? userInfo?.countryCode,
				phoneNumber: dummyData?.details?.phoneNumber ?? userInfo?.phoneNumber,
			};
			ARRAY_FIELDS?.map((slugName: any) => {
				dummyData[slugName] = [...dummyData[slugName], ...RESUME_DEFAULT_VALUES[slugName]];
			});
			updateResumeLimits(templateData?.limits);
			setStateResumeForm({
				...dummyData,
				details: {
					...dummyData.details,
					...userData,
				},
				profilePicture: dummyData?.profilePicture ?? userInfo?.profilePicture,
			});
		}
	}, [templateData]);

	useEffect(() => {
		const debouncedFunc = debounce(() => {
			updateResumeData(stateResumeForm);
		}, 1000);

		if (fieldType && ['text', 'email', 'textarea'].includes(fieldType)) {
			debouncedFunc();
		} else {
			updateResumeData(stateResumeForm);
		}

		return () => debouncedFunc.cancel();
	}, [stateResumeForm]);

	const handleModalVisibility = (slug: string, index: number, formikObj: any) => {
		if (showConfirm) {
			setRemoveAlertData({
				deleteIndex: undefined,
				deleteSlug: undefined,
				formikObj: undefined,
			});
			setShowConfirm(!showConfirm);
		} else {
			setRemoveAlertData({
				deleteIndex: index ?? undefined,
				deleteSlug: slug ?? undefined,
				formikObj,
			});
			setShowConfirm(!showConfirm);
		}
	};

	const handleModalConfirm = () => {
		setShowConfirm(!showConfirm);
		handleRemoveClick(removeAlertData?.deleteSlug, removeAlertData?.deleteIndex);
		removeAlertData?.formikObj?.resetForm();
	};

	const getCommonObj = (slug: any, id: any) => {
		return commonData?.[SLUGS[slug]]?.find((item: any) => item.id === Number(id));
	};

	const updateAddressInfo = (name: any, value: any) => {
		switch (name) {
			case 'countryId':
				return {
					countryName: value,
					stateId: '',
					cityId: '',
					stateName: '',
					cityName: '',
				};
			case 'stateId':
				return {
					stateName: value,
					cityId: '',
					cityName: '',
				};
			case 'cityId':
				return {
					cityName: value,
				};
		}
	};

	const onHandleChange = (slug: string, event: any, index: number) => {
		const elementName = event.target.name;
		const elementValue =
			event.target.type === 'checkbox' ? event.target.checked : event.target.value;
		const selectInnerText = event.target.innerText ?? '';
		setFieldType(event.target.type);
		if (index !== undefined) {
			const checkIndex = index < 0 ? 0 : index;
			const updatedDataItem = { ...stateResumeForm[slug][checkIndex] };
			if (elementName === 'address') {
				updatedDataItem['countryId'] = Number(elementValue?.countryId);
				updatedDataItem['stateId'] = Number(elementValue?.stateId);
				updatedDataItem['cityId'] = Number(elementValue?.cityId);
				updatedDataItem['address'] = elementValue?.location;
			} else {
				updatedDataItem[elementName] = elementValue;
			}

			const updatedData = [...stateResumeForm[slug]];
			updatedData[checkIndex] = {
				...updatedDataItem,
				...updateAddressInfo(elementName, selectInnerText),
			};

			setStateResumeForm({
				...stateResumeForm,
				[slug]: updatedData,
			});
		} else {
			setStateResumeForm({
				...stateResumeForm,
				[slug]: ['skills', 'tools']?.includes(slug)
					? getCommonObj(slug, elementValue)
						? [...stateResumeForm[slug], getCommonObj(slug, elementValue)]
						: stateResumeForm[slug]
					: elementName === 'address'
						? {
								...stateResumeForm[slug],
								countryId: Number(elementValue?.countryId),
								stateId: Number(elementValue?.stateId),
								cityId: Number(elementValue?.cityId),
								address: elementValue?.location,
							}
						: elementName !== slug && elementName
							? elementName === 'industryId'
								? {
										...stateResumeForm[slug],
										[elementName]: Number(elementValue),
										industryName: getCommonObj('industries', elementValue)?.title,
									}
								: {
										...stateResumeForm[slug],
										[elementName]: elementValue,
									}
							: elementValue,
			});
		}
	};

	const handleTitleChange = (slug: string, value: string) => {
		setStateResumeForm({
			...stateResumeForm,
			[slug]: value,
		});
	};

	const onChangeAdditionalField = (slug: string, id: number, additionalData: any) => {
		if (typeof additionalData === 'object') {
			const selectedData = commonData[SLUGS[slug]]?.find((item: any) => {
				return item?.id === Number(id);
			});

			const slugData = [
				...stateResumeForm[slug],
				{
					...selectedData,
					socialLink: additionalData?.socialLink,
					title: additionalData.title,
					id: id,
					image: additionalData?.icon,
				},
			];

			setStateResumeForm({
				...stateResumeForm,
				[slug]: slugData,
			});
		} else if (id && additionalData) {
			const selectedData = commonData[SLUGS[slug]]?.find((item: any) => {
				return item?.id === Number(id);
			});

			const slugData = [
				...stateResumeForm[slug],
				{
					...selectedData,
					[slug === 'language' ? 'proficiency' : 'socialLink']: additionalData,
				},
			];

			setStateResumeForm({
				...stateResumeForm,
				[slug]: slugData,
			});
		}
	};

	const handleRemoveValues = (slug: string, id: any) => {
		id &&
			setStateResumeForm({
				...stateResumeForm,
				[slug]: stateResumeForm[slug]?.filter((item: any) => item.id !== Number(id)),
			});
	};

	const handleAddMoreClick = (slug: any) => {
		setStateResumeForm({
			...stateResumeForm,
			[slug]: [...stateResumeForm[slug], ...RESUME_DEFAULT_VALUES[slug]],
		});
	};

	const handleRemoveClick = async (slug: any, index?: number) => {
		const fieldVisibility = stateResumeForm['manageTemplateFields'][slug];
		if (fieldVisibility) {
			const finalData: any = {};
			const slugUpdatedData = index
				? stateResumeForm[slug].filter((_: any, idx: number) => idx !== Number(index))
				: [];

			finalData['manageTemplateFields'] = {
				...stateResumeForm['manageTemplateFields'],
				[slug]: index !== undefined ? fieldVisibility : !fieldVisibility,
			};

			try {
				const response = await saveResumeMutation({
					...finalData,
					[slug]: slugUpdatedData,
					templateId: Number(templateSlug),
				});

				if (response?.success) {
					updateDeletedState(fieldVisibility, slug, index);
				}
			} catch (err) {
				console.log(err);
			}
		} else {
			updateDeletedState(fieldVisibility, slug, index);
		}
	};

	const updateDeletedState = (fieldVisibility: any, slug: string, index?: number) => {
		setStateResumeForm({
			...stateResumeForm,
			[slug]:
				index !== undefined
					? stateResumeForm[slug].filter((_: any, idx: number) => idx !== Number(index))
					: fieldVisibility
						? []
						: RESUME_DEFAULT_VALUES[slug],
			manageTemplateFields: {
				...stateResumeForm['manageTemplateFields'],
				[slug]: index !== undefined ? fieldVisibility : !fieldVisibility,
			},
		});
	};

	const handleFileChange = async ({
		slug,
		event,
		fileType = 'image/',
		fileSize = 10,
		index,
	}: any) => {
		setIsUploading({
			...isUploading,
			[slug]: true,
		});
		const file: any = event.target.files?.[0];
		if (file) {
			const pngFileData: any = await compressFileSize(file);

			const imgPath = await imageBucketUrl(
				pngFileData,
				translate,
				'image/png',
				fileSize,
				bucketUrlMutation,
				uploadFileMutation,
			);
			if (imgPath) {
				if (index !== undefined) {
					const updatedDataItem = { ...stateResumeForm[slug][index] };
					updatedDataItem[event.target.name] = imgPath;
					const updatedData = [...stateResumeForm[slug]];
					updatedData[index] = updatedDataItem;
					setStateResumeForm({
						...stateResumeForm,
						[slug]: updatedData,
					});
				} else {
					setStateResumeForm({
						...stateResumeForm,
						[slug]: imgPath,
					});
				}
			}
		}
		setTimeout(() => {
			setIsUploading({
				...isUploading,
				[slug]: false,
			});
		}, 1500);
	};

	const checkLimitExceed = (slug: string) => {
		if (!stateResumeForm?.manageTemplateFields?.[slug]) return false;
		if (slug === 'hobbies') {
			return Number(resumeData[slug]?.length || 0) < Number(resumeLimits[slug]);
		} else if (ARRAY_FIELDS?.includes(slug)) {
			return Number(stateResumeForm[slug]?.length || 0) > Number(resumeLimits[slug]);
		} else {
			return Number(stateResumeForm[slug]?.length || 0) < Number(resumeLimits[slug]);
		}
	};

	return (
		<>
			<div className="build-resume-top">
				<div className="mb-3">
					<Link to="/seeker/resume-templates" className="text-decoration-none p-0 back-btn me-5">
						<i className="ri-arrow-left-line me-2"></i>
						{translate('Template.backToTemplate')}
					</Link>
				</div>
				<div className="d-sm-flex align-items-center justify-content-center">
					<h4 className="build-resume-top-heading mb-2 mb-sm-3 fw-bold text-center">
						{translate('Template.templatePreview')}
					</h4>
				</div>
				<CustomProgressBar />
			</div>
			<div className="build-resume">
				<ConfirmModal
					showConfirm={showConfirm}
					handleModalVisibility={handleModalVisibility}
					handleModalConfirm={handleModalConfirm}
					slug={removeAlertData?.deleteSlug}
				/>
				<div className="build-resume-edit">
					{SHOW_PROFILE_PICTURE && (
						<Profile
							data={profile}
							slug="profilePicture"
							handleRemoveClick={handleRemoveClick}
							isUploading={isUploading}
							handleTitleChange={handleTitleChange}
							handleFileChange={handleFileChange}
						/>
					)}

					{SHOW_DETAILS && (
						<PersonalInfo
							data={details}
							slug="details"
							onHandleChange={onHandleChange}
							handleTitleChange={handleTitleChange}
						/>
					)}

					{SHOW_SHORTBIO && (
						<ShortBio
							data={shortBio}
							slug="shortBio"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleTitleChange={handleTitleChange}
							setStateResumeForm={setStateResumeForm}
							stateResumeForm={stateResumeForm}
						/>
					)}

					{SHOW_EXPERIENCE && (
						<Experience
							data={experience}
							slug="experience"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleFileChange={handleFileChange}
							handleModalVisibility={handleModalVisibility}
							isUploading={isUploading}
							handleTitleChange={handleTitleChange}
							showExperienceImage={SHOW_EXPERIENCE_IMAGE}
							showExperienceRoleandresponsibilities={SHOW_EXPERIENCE_ROLEANDRESPONSIBILITIES}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_PROJECT && (
						<ProjectsInfo
							data={project}
							slug="project"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
							setStateResumeForm={setStateResumeForm}
						/>
					)}

					{SHOW_EDUCATION && (
						<EducationInfo
							data={education}
							slug="education"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleFileChange={handleFileChange}
							handleModalVisibility={handleModalVisibility}
							isUploading={isUploading}
							handleTitleChange={handleTitleChange}
							showEducationImage={SHOW_EDUCATION_IMAGE}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_SKILLS && (
						<Skills
							data={skills}
							slug="skills"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleRemoveValues={handleRemoveValues}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_TOOLS && (
						<Tools
							data={tools}
							slug="tools"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleRemoveValues={handleRemoveValues}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_AWARDS && (
						<Achievements
							data={awards}
							slug="awards"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleFileChange={handleFileChange}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							showAwardImage={SHOW_AWARD_IMAGE}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_CERTIFICATION && (
						<CertificationsInfo
							data={certification}
							slug="certification"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onHandleChange}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							showCertificationsImage={SHOW_CERTIFICATIONS_IMAGE}
							handleFileChange={handleFileChange}
							isUploading={isUploading}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_LANGUAGE && (
						<Language
							data={language}
							slug="language"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onChangeAdditionalField}
							handleRemoveValues={handleRemoveValues}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_SOCIAL && (
						<SocialMedia
							data={social}
							slug="social"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							onHandleChange={onChangeAdditionalField}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
						/>
					)}

					{SHOW_HOBBIES && (
						<Hobbies
							data={hobbies}
							slug="hobbies"
							handleAddMoreClick={handleAddMoreClick}
							handleRemoveClick={handleRemoveClick}
							stateResumeForm={stateResumeForm}
							setStateResumeForm={setStateResumeForm}
							handleModalVisibility={handleModalVisibility}
							handleTitleChange={handleTitleChange}
							checkLimitExceed={checkLimitExceed}
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default ResumeForm;
